import {Box, Divider, Flex, Text, VStack} from '@chakra-ui/react';
import {colors, hiddenScrollBarStyles} from 'components/providers';
import {BadgePercent, Heart} from 'lucide-react';
import {useRouter} from 'next/router';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import NextImageUI from 'ui/NextImage';

import {useTranslation} from '../../hooks';
import {TranslationKeys} from '../../translations';
import HoverableChevron from './components/HoverableChevron';
import SidebarSkeleton from './components/SidebarSkeleton';
import {IMetroCategory} from './types';

interface MetroSidebarProps {
  categories: IMetroCategory[] | undefined;
  isLoading: boolean;
}

const HOVER_DELAY = 150;
const HIDE_DELAY = 500; // delay before hiding sidebar
const COLUMN_WIDTH = '260px';
const SPACING = '15px';

const useHoverTimeout = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHoverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setHoverTimeout = useCallback(
    (callback: () => void, delay = HOVER_DELAY) => {
      clearHoverTimeout();
      timeoutRef.current = setTimeout(callback, delay);
    },
    [clearHoverTimeout],
  );

  useEffect(() => () => clearHoverTimeout(), [clearHoverTimeout]);

  return {clearHoverTimeout, setHoverTimeout};
};

const CategoryIcon: FC<{src: string | null; alt: string; size?: string}> = ({src, alt, size = '25px'}) => {
  const sizeInPx = parseInt(size);

  return (
    <Box
      width={size}
      height={size}
      borderRadius="6px"
      overflow="hidden"
      bg={colors.lightGray}
      display="flex"
      alignItems="center"
      justifyContent="center"
      mr="10px"
      position="relative"
    >
      {src ? (
        <NextImageUI src={src} alt={alt} width={sizeInPx} height={sizeInPx} />
      ) : (
        <NextImageUI src="/food_pattern.webp" alt={alt} fill style={{objectFit: 'cover'}} />
      )}
    </Box>
  );
};

const FavoritesIcon: FC<{size?: string}> = ({size = '25px'}) => (
  <Box
    width={size}
    height={size}
    borderRadius="6px"
    overflow="hidden"
    bg={colors.lightGray}
    display="flex"
    alignItems="center"
    justifyContent="center"
    mr="10px"
    position="relative"
  >
    <Heart size={16} color={colors.pink} fill={colors.pink} />
  </Box>
);

const PromoIcon: FC<{size?: string}> = ({size = '25px'}) => (
  <Box
    width={size}
    height={size}
    borderRadius="6px"
    overflow="hidden"
    bg={colors.lightGray}
    display="flex"
    alignItems="center"
    justifyContent="center"
    mr="10px"
    position="relative"
  >
    <BadgePercent size={16} color={colors.pink} />
  </Box>
);

const CategoryItem: FC<{
  category: IMetroCategory;
  isActive?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  showIcon?: boolean;
  bold?: boolean;
}> = ({category, isActive, onClick, onMouseEnter, showIcon = true, bold = false}) => (
  <Flex
    alignItems="center"
    p="8px"
    borderRadius="8px"
    _hover={{bg: colors.lightGray}}
    cursor="pointer"
    bg={isActive ? colors.lightGray : 'transparent'}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    justify="space-between"
    className="sidebar-item-content"
  >
    <Flex alignItems="center" flex={1}>
      {showIcon && <CategoryIcon src={category.img_small_thumbnail} alt={`${category.name} icon`} />}
      <Text fontWeight={bold ? '600' : '400'} fontSize="13px" noOfLines={1}>
        {category.name}
      </Text>
    </Flex>
    {category.children?.length > 0 && <HoverableChevron />}
  </Flex>
);

const FavoritesItem: FC<{
  isActive?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  bold?: boolean;
  t: (term: TranslationKeys) => string;
}> = ({isActive, onClick, t, onMouseEnter, bold = false}) => (
  <Flex
    alignItems="center"
    p="8px"
    borderRadius="8px"
    _hover={{bg: colors.lightGray}}
    cursor="pointer"
    bg={isActive ? colors.lightGray : 'transparent'}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    justify="space-between"
    className="sidebar-item-content"
  >
    <Flex alignItems="center" flex={1}>
      <FavoritesIcon />
      <Text fontWeight={bold ? '600' : '400'} fontSize="13px" noOfLines={1}>
        {t('FAVORITES')}
      </Text>
    </Flex>
  </Flex>
);

const PromoItem: FC<{
  isActive?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  bold?: boolean;
  t: (term: TranslationKeys) => string;
}> = ({isActive, onClick, t, onMouseEnter, bold = false}) => (
  <Flex
    alignItems="center"
    p="8px"
    borderRadius="8px"
    _hover={{bg: colors.lightGray}}
    cursor="pointer"
    bg={isActive ? colors.lightGray : 'transparent'}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    justify="space-between"
    className="sidebar-item-content"
  >
    <Flex alignItems="center" flex={1}>
      <PromoIcon />
      <Text fontWeight={bold ? '600' : '400'} fontSize="13px" noOfLines={1}>
        {t('PROFITABLE_SHELF')}
      </Text>
    </Flex>
  </Flex>
);

const ColumnHeader: FC<{category: IMetroCategory}> = ({category}) => (
  <Flex alignItems="center" mb="15px">
    <CategoryIcon
      key={`${category.id}-${category.img_small_thumbnail}`}
      src={category.img_small_thumbnail}
      alt={`${category.name} icon`}
      size="32px"
    />
    <Text fontWeight="600" fontSize="16px" color={colors.black} noOfLines={1} flex={1}>
      {category.name}
    </Text>
  </Flex>
);

const SidebarColumn: FC<{
  width?: string;
  borderRight?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: React.ReactNode;
}> = ({width = COLUMN_WIDTH, borderRight = false, onMouseEnter, onMouseLeave, children}) => (
  <Box
    width={width}
    p={SPACING}
    {...(borderRight && {borderRight: '1px solid', borderColor: 'gray.100'})}
    overflowY="auto"
    height="100%"
    sx={hiddenScrollBarStyles}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <Box pt="10px">{children}</Box>
  </Box>
);

const MetroSidebar: FC<MetroSidebarProps> = ({categories, isLoading}) => {
  const {push} = useRouter();
  const {t} = useTranslation();
  const [hoveredCategory, setHoveredCategory] = useState<IMetroCategory | null>(null);
  const [hoveredSubcategory, setHoveredSubcategory] = useState<IMetroCategory | null>(null);

  const categoryTimeout = useHoverTimeout();
  const subcategoryTimeout = useHoverTimeout();
  const hideTimeout = useHoverTimeout(); // Timeout for hiding entire sidebar

  const handleCategoryClick = useCallback((category: IMetroCategory) => push(`/categories/${category.slug}`), [push]);
  const handleFavoritesClick = useCallback(() => push('/favorite-product'), [push]);
  const handlePromoClick = useCallback(() => push('/market-category/promo'), [push]);

  const handleCategoryMouseEnter = useCallback(
    (category: IMetroCategory) => {
      categoryTimeout.clearHoverTimeout();
      setHoveredCategory(category);
    },
    [categoryTimeout],
  );

  const handleCategoryMouseLeave = useCallback(() => {
    categoryTimeout.setHoverTimeout(() => {
      setHoveredCategory(null);
      setHoveredSubcategory(null);
    });
  }, [categoryTimeout]);

  const handleSubcategoryMouseEnter = useCallback(
    (subcategory: IMetroCategory) => {
      subcategoryTimeout.clearHoverTimeout();
      setHoveredSubcategory(subcategory);
    },
    [subcategoryTimeout],
  );

  const handleSubcategoryMouseLeave = useCallback(() => {
    subcategoryTimeout.setHoverTimeout(() => setHoveredSubcategory(null));
  }, [subcategoryTimeout]);

  const preventCategoryHide = useCallback(() => {
    categoryTimeout.clearHoverTimeout();
  }, [categoryTimeout]);

  const preventSubcategoryHide = useCallback(() => {
    subcategoryTimeout.clearHoverTimeout();
  }, [subcategoryTimeout]);

  // Functions to handle 2-second hide delay
  const startHideTimer = useCallback(() => {
    hideTimeout.setHoverTimeout(() => {
      setHoveredCategory(null);
      setHoveredSubcategory(null);
    }, HIDE_DELAY);
  }, [hideTimeout]);

  const cancelHideTimer = useCallback(() => {
    hideTimeout.clearHoverTimeout();
  }, [hideTimeout]);

  // Enhanced mouse leave handlers with hide timer
  const handleSidebarMouseLeave = useCallback(() => {
    startHideTimer();
  }, [startHideTimer]);

  const handleSidebarMouseEnter = useCallback(() => {
    cancelHideTimer();
  }, [cancelHideTimer]);

  if (!categories && !isLoading) return null;

  return (
    <Box
      bg={colors.white}
      borderRadius="16px"
      height="90vh"
      overflow="visible"
      position="relative"
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      {isLoading ? (
        <Box p={SPACING} flex="1" height="100%" overflowY="auto" sx={hiddenScrollBarStyles}>
          <SidebarSkeleton />
        </Box>
      ) : (
        <>
          {/* Main Categories Column - stays within reserved space */}
          <Box height="100%" overflowY="auto" sx={hiddenScrollBarStyles}>
            <SidebarColumn borderRight>
              <VStack align="stretch" spacing="1">
                <Box>
                  <FavoritesItem isActive={false} onClick={handleFavoritesClick} bold={true} t={t} />
                </Box>

                <Box>
                  <PromoItem isActive={false} onClick={handlePromoClick} bold={true} t={t} />
                </Box>

                <Divider my="4px" />

                {categories?.map((category) => (
                  <Box
                    key={category.id}
                    onMouseEnter={() => handleCategoryMouseEnter(category)}
                    onMouseLeave={handleCategoryMouseLeave}
                  >
                    <CategoryItem
                      category={category}
                      isActive={hoveredCategory?.id === category.id}
                      onClick={() => handleCategoryClick(category)}
                      onMouseEnter={() => handleCategoryMouseEnter(category)}
                      bold={true}
                    />
                  </Box>
                ))}
              </VStack>
            </SidebarColumn>
          </Box>

          {/* Subcategories Column - positioned absolutely relative to viewport */}
          {hoveredCategory && hoveredCategory?.children?.length > 0 && (
            <Box
              position="absolute"
              top="0"
              left="260px"
              height="90vh"
              width="260px"
              bg={colors.white}
              borderRadius="16px"
              boxShadow="xl"
              zIndex={1001}
              border="1px solid"
              borderColor="gray.100"
              overflowY="auto"
              sx={hiddenScrollBarStyles}
              onMouseEnter={handleSidebarMouseEnter}
              onMouseLeave={handleSidebarMouseLeave}
            >
              <SidebarColumn onMouseEnter={preventCategoryHide} onMouseLeave={handleSubcategoryMouseLeave}>
                <ColumnHeader category={hoveredCategory} />
                <Divider mb={1} />
                <VStack align="stretch" spacing="1">
                  {hoveredCategory?.children?.map((subcategory) => (
                    <Box key={subcategory.id} onMouseEnter={() => handleSubcategoryMouseEnter(subcategory)}>
                      <CategoryItem
                        category={subcategory}
                        isActive={hoveredSubcategory?.id === subcategory.id}
                        onClick={() => handleCategoryClick(subcategory)}
                        onMouseEnter={() => handleSubcategoryMouseEnter(subcategory)}
                        showIcon={false}
                      />
                    </Box>
                  ))}
                </VStack>
              </SidebarColumn>
            </Box>
          )}

          {/* Sub-subcategories Column - positioned absolutely relative to viewport */}
          {hoveredSubcategory && hoveredSubcategory?.children?.length > 0 && (
            <Box
              position="absolute"
              top="0"
              left="520px"
              height="90vh"
              width="260px"
              bg={colors.white}
              borderRadius="16px"
              boxShadow="xl"
              zIndex={1002}
              border="1px solid"
              borderColor="gray.100"
              overflowY="auto"
              sx={hiddenScrollBarStyles}
              onMouseEnter={handleSidebarMouseEnter}
              onMouseLeave={handleSidebarMouseLeave}
            >
              <SidebarColumn onMouseEnter={preventSubcategoryHide}>
                <ColumnHeader category={hoveredSubcategory} />
                <Divider mb={1} />
                <VStack align="stretch" spacing="1">
                  {hoveredSubcategory?.children?.map((subSubcategory) => (
                    <CategoryItem
                      key={subSubcategory.id}
                      category={subSubcategory}
                      onClick={() => handleCategoryClick(subSubcategory)}
                      showIcon={false}
                    />
                  ))}
                </VStack>
              </SidebarColumn>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MetroSidebar;
